import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Logo } from "./Logo";
import { useLang } from "../../context/LangContext";
import { useCart } from "../../context/CartContext";
import { SearchDialog } from "./SearchDialog";
import { useAuth } from "../../context/AuthContext";

export const NAV = [
  ["nav_home", "/"],
  ["nav_all", "/shop/all"],
  ["nav_daily", "/shop/daily"],
  ["nav_luxury", "/shop/luxury"],
  ["nav_new", "/shop/new"],
  ["nav_offers", "/shop/offers"],
];

const IconBtn = ({ children, ...p }) => (
  <button className="relative grid h-9 w-9 place-items-center transition-colors duration-300 hover:text-gold-deep sm:h-10 sm:w-10" {...p}>
    {children}
  </button>
);

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { user } = useAuth();
  const { count, setOpen, wishlist } = useCart();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const nav = useNavigate();

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 border-b border-sand/50 bg-ivory/80 backdrop-blur-xl">
      <div className="container-q flex h-[72px] items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-1 lg:hidden">
          <IconBtn data-testid="mobile-menu-button" onClick={() => setMenu(true)} aria-label="menu"><Menu size={20} strokeWidth={1.4} /></IconBtn>
          <IconBtn data-testid="mobile-search-button" onClick={() => setSearch(true)} aria-label="search"><Search size={19} strokeWidth={1.4} /></IconBtn>
        </div>
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map(([k, to]) => (
            <NavLink key={k} to={to} end data-testid={`nav-${k}`}
              className={({ isActive }) => `link-u py-1 text-[13px] tracking-[0.08em] ${isActive ? "text-gold-deep" : "text-ink"}`}>
              {t(k)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-0.5">
          <button data-testid="lang-toggle" onClick={toggle} className="me-1 hidden px-2 text-[12px] tracking-[0.2em] transition-colors hover:text-gold-deep sm:block">
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          <span className="hidden lg:block"><IconBtn data-testid="header-search-button" onClick={() => setSearch(true)} aria-label="search"><Search size={19} strokeWidth={1.4} /></IconBtn></span>
          <span className="sm:hidden"><IconBtn data-testid="mobile-account-button" onClick={() => nav("/account")} aria-label="account"><User size={19} strokeWidth={1.4} /></IconBtn></span>
          <button data-testid="header-account-button" onClick={() => nav("/account")}
            className={`mx-1 hidden items-center gap-2 px-4 py-2 text-[12px] tracking-[0.1em] transition-colors duration-300 sm:flex ${user ? "hover:text-gold-deep" : "border border-ink hover:bg-ink hover:text-ivory"}`}>
            <User size={16} strokeWidth={1.4} />
            {user ? user.name?.split(" ")[0] : t("sign_in")}
          </button>
          <IconBtn data-testid="header-wishlist-button" onClick={() => nav("/wishlist")} aria-label="wishlist">
            <Heart size={19} strokeWidth={1.4} />
            {wishlist.length > 0 && <span className="absolute end-1 top-1 h-1.5 w-1.5 rounded-full bg-gold" />}
          </IconBtn>
          <IconBtn data-testid="header-cart-button" onClick={() => setOpen(true)} aria-label="cart">
            <ShoppingBag size={19} strokeWidth={1.4} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -end-0.5 top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-ink px-1 text-[10px] text-ivory">
                {count}
              </span>
            )}
          </IconBtn>
        </div>
      </div>

      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side={lang === "ar" ? "right" : "left"} className="w-[86vw] max-w-sm border-sand bg-ivory p-8">
          <SheetTitle className="sr-only">menu</SheetTitle>
          <Logo />
          <div className="mt-12 flex flex-col">
            {NAV.map(([k, to], i) => (
              <Link key={k} to={to} onClick={() => setMenu(false)} data-testid={`mobile-nav-${k}`}
                className="flex items-baseline gap-4 border-b border-sand/60 py-5 font-display text-2xl">
                <span className="font-latin text-xs text-gold-deep">0{i + 1}</span>
                {t(k)}
              </Link>
            ))}
            <Link to="/account" onClick={() => setMenu(false)} data-testid="mobile-nav-account"
              className={`mt-8 flex items-center justify-center gap-2 py-4 text-sm tracking-[0.1em] ${user ? "border border-ink" : "bg-ink text-ivory"}`}>
              <User size={16} strokeWidth={1.4} />{user ? `${t("account")} · ${user.name?.split(" ")[0]}` : t("sign_in")}
            </Link>
            <div className="mt-5 flex items-center justify-between text-sm">
              <Link to="/track" onClick={() => setMenu(false)} className="text-sand-dark">{t("track_order")}</Link>
              <button data-testid="mobile-lang-toggle" onClick={toggle} className="tracking-[0.2em]">{lang === "ar" ? "English" : "عربي"}</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <SearchDialog open={search} onOpenChange={setSearch} />
    </header>
  );
};
